import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, AudioLines, Eye, EyeOff, Film, Layers, Link2, Plus, Trash2, Volume2, VolumeX } from 'lucide-react';
import {
  MAX_PX_PER_SEC, MIN_GAIN_DB, MIN_PX_PER_SEC, clipDuration, clipEnd, clipGainDb, dbToGain, exportRange, formatFrameTimecode, formatGainDb, formatTimecode, linkedGroup, moveClips, placeOnTrack, projectDuration,
  removeTrack, setClipGain, setInPoint, setOutPoint, setTrackFlag, setTrackLevel, snapMove, snapPoints, snapTime, splitClip, trackLevel, trimClip,
  type EditorClip, type EditorTrack, type InsertPlacement, type QuickEditorProject, type TrackKind,
} from '../../../utils/quickEditor';
import { MediaSlider } from '../../VideoPlayer';
import { ClipWaveform } from './ClipWaveform';

export type EditorTool = 'select' | 'razor';

/** Media being dragged from the editor's list: what it is and how long, to show where it would land. */
export interface MediaDrag {
  kind: TrackKind;
  durationSec: number;
}

/** Where dragged media was dropped: a time, and a track or a new one. */
export type MediaDrop = InsertPlacement & { timeSec: number };

interface EditorTimelineProps {
  project: QuickEditorProject;
  selectedIds: string[];
  tool: EditorTool;
  /** Assets whose media is not available in this browser; their clips are dimmed. */
  missingAssetIds: Set<string>;
  disabled?: boolean;
  getTime: () => number;
  /** Hands the timeline's playhead mover to the playback engine (null on unmount). */
  registerPlayhead: (move: ((timeSec: number, playing: boolean) => void) | null) => void;
  onSeek: (timeSec: number) => void;
  /** The timeline's frame rate: ruler ticks fall on frames. */
  frameRate: number;
  onSelect: (clipIds: string[]) => void;
  /** A change shown while a drag is in progress; not yet part of the edit. */
  onPreview: (next: QuickEditorProject) => void;
  /** A finished change. */
  onCommit: (next: QuickEditorProject) => void;
  onCancelPreview: () => void;
  onZoom: (pxPerSec: number) => void;
  makeId: () => string;
  /** Larger tracks and a timeline that keeps a share of the height: for the full Edit page. */
  roomy?: boolean;
  /**
   * Media dragged from the list; any lane takes the drop (the clip goes to a track of its own
   * kind at that time), and a new-layer strip appears.
   */
  mediaDrag?: MediaDrag | null;
  onDropMedia?: (drop: MediaDrop) => void;
  /** Files dragged from the device and dropped on the timeline, where they were dropped. */
  onDropFiles?: (files: File[], drop: MediaDrop) => void;
  /** Peaks per asset with sound, drawn on its audio clips to show where to cut. */
  waveforms?: Record<string, Float32Array>;
  /** Whether each asset's sound is still being read, or has none that can be read. */
  waveformStatus?: Record<string, 'reading' | 'ready' | 'none'>;
}

/** What the timeline can be asked to do from outside */
export interface EditorTimelineHandle {
  /** Zoom so the whole edit is in view */
  fitToView: () => void;
}

/** The lane of a drop on the empty bed under the tracks, or on the ruler: the clip goes on the first free track of its kind */
const BED_LANE = '__bed__';
/** Dragged files are of a length not yet known; the drop preview is this long */
const FILE_DRAG_SEC = 5;

const RULER_HEIGHT = 22;
const NEW_LANE_HEIGHT = 28;
const SIZES = {
  compact: { header: 96, track: { video: 46, audio: 42 } as Record<TrackKind, number> },
  roomy: { header: 140, track: { video: 64, audio: 50 } as Record<TrackKind, number> },
};
/** The custom drag type for media from the editor's list. */
export const EDITOR_MEDIA_DRAG = 'application/x-noodle-editor-media';
const SNAP_PX = 8;
/** The lane id of the strip that makes a new track for dropped media. */
const NEW_LANE = '__new__';
/** Empty time kept after the last clip so there is room to drop and to scrub. */
const TAIL_SEC = 6;
/** Ruler steps: under a second they are whole frames (frame counts that divide a second evenly where they can), then whole seconds. */
const SECOND_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
const FRAME_STEPS = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15];
const rulerSteps = (fps: number): number[] => [...FRAME_STEPS.filter(n => n < fps).map(n => n / fps), ...SECOND_STEPS];
/** Frame ticks show once a frame is at least this wide. */
const FRAME_TICK_MIN_PX = 8;

const clamp = (value: number, lo: number, hi: number) => Math.min(Math.max(value, lo), hi);

/**
 * A round button on the editor's ink surface: clear, lighter under the pointer, and lavender
 * while it is on (aria-pressed). The ring of the keyboard focus is the light lavender, which
 * shows on ink.
 * (The two aria-pressed utilities repeat what ns-icon-btn-on-dark says: in a node's card the
 * rule that gives a pressed button the node's tone outweighs it, and the editor's tone is ink.)
 */
export const EDITOR_ICON_BTN = 'ns-icon-btn ns-icon-btn-on-dark aria-pressed:bg-lavender aria-pressed:text-ink focus-visible:outline-lavender';
/** The same for what removes something: faint, and red once the pointer is on it. */
export const EDITOR_REMOVE_BTN = 'ns-icon-btn ns-icon-btn-xs ns-icon-btn-on-dark text-ink-faint hover:bg-danger/20 hover:text-danger focus-visible:outline-lavender';

interface Drag {
  mode: 'move' | 'trim-start' | 'trim-end' | 'seek' | 'gain' | 'in' | 'out' | 'box';
  pointerId: number;
  clipId?: string;
  kind?: TrackKind;
  originX: number;
  originY: number;
  originStart: number;
  originEnd: number;
  /** Gain drags: the clip's gain at the start, and the on-screen height of its whole range. */
  originDb?: number;
  spanPx?: number;
  base: QuickEditorProject;
  moved: boolean;
  last: QuickEditorProject | null;
  /** Moves: the other selected clips that move with the grabbed one. */
  together?: string[];
  /** A click on one of several selected clips, without a drag, leaves just that clip selected. */
  collapseTo?: string;
  /** Selection boxes: where the box started, in the timeline's own pixels, and what stays selected besides the clips it touches. */
  boxOrigin?: { x: number; y: number };
  keep?: string[];
}

/** Where a clip's volume line sits, from its top: 0 dB near the top, MIN_GAIN_DB at the bottom. */
const GAIN_LINE_TOP = 3;
const GAIN_LINE_BOTTOM = 3;
const gainLineY = (db: number, height: number): number =>
  GAIN_LINE_TOP + (db / MIN_GAIN_DB) * (height - GAIN_LINE_TOP - GAIN_LINE_BOTTOM);
const formatDb = formatGainDb;

/**
 * The timeline: a ruler, a lane per track with its clips, and the playhead.
 * Drag a clip to move it (it snaps to other clips and the playhead and can
 * change to another track of its kind), drag an edge to trim, and with the
 * razor tool click a clip to cut it. Shift or Ctrl and a click adds a clip to the selection
 * or takes it out, and a drag across empty lane space selects the clips it touches; the
 * selected clips then move together. The ruler scrubs, and a click on empty lane space
 * moves the playhead there.
 */
export const EditorTimeline = forwardRef<EditorTimelineHandle, EditorTimelineProps>(({
  project, selectedIds, tool, missingAssetIds, disabled = false, getTime, registerPlayhead,
  onSeek, frameRate, onSelect, onPreview, onCommit, onCancelPreview, onZoom, makeId, roomy = false, mediaDrag = null, onDropMedia, onDropFiles, waveforms = {},
  waveformStatus = {},
}, handle) => {
  const pps = project.pxPerSec;
  const { header: headerWidth, track: trackHeight } = roomy ? SIZES.roomy : SIZES.compact;
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const playheadEl = useRef<HTMLDivElement>(null);
  const laneEls = useRef(new Map<string, HTMLDivElement>());
  const dragRef = useRef<Drag | null>(null);
  const zoomAnchorRef = useRef<{ time: number; offsetX: number } | null>(null);
  const [viewportWidth, setViewportWidth] = useState(600);

  const duration = projectDuration(project);
  const range = exportRange(project);
  const contentWidth = Math.max(viewportWidth, (duration + TAIL_SEC) * pps);
  const assetsById = useMemo(() => new Map(project.assets.map(asset => [asset.id, asset])), [project.assets]);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  // A level slider being dragged: the edit it started from, and the latest preview
  const levelDrag = useRef<{ base: QuickEditorProject; last: QuickEditorProject } | null>(null);
  // Where media dragged from the list would land: a lane (a track, or the new-layer strip) and a time
  const [dropHint, setDropHint] = useState<{ lane: string; timeSec: number } | null>(null);
  // Files from the device being dragged over the timeline
  const [fileDrag, setFileDrag] = useState(false);
  useEffect(() => { if (!mediaDrag && !fileDrag) setDropHint(null); }, [mediaDrag, fileDrag]);
  /** What is being dragged over the timeline, if anything: media from the list, or files */
  const dragged: MediaDrag | null = mediaDrag ?? (fileDrag ? { kind: 'video', durationSec: FILE_DRAG_SEC } : null);
  const acceptsDrops = !disabled && ((!!mediaDrag && !!onDropMedia) || !!onDropFiles);
  const isFileDrag = (e: React.DragEvent) => !mediaDrag && Array.from(e.dataTransfer.types).includes('Files');

  useImperativeHandle(handle, () => ({
    fitToView: () => {
      const width = scrollRef.current?.clientWidth ?? 0;
      const whole = projectDuration(project);
      if (width <= 0 || whole <= 0) return;
      // The edit and a little room after it, seen from its start
      onZoom(Math.min(MAX_PX_PER_SEC, Math.max(MIN_PX_PER_SEC, (width - 16) / (whole * 1.02))));
      if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    },
  }), [project, onZoom]);
  // A clip's gain while its volume line is dragged, shown next to the line
  const [gainReadout, setGainReadout] = useState<{ clipId: string; db: number } | null>(null);
  const lastGainPress = useRef<{ clipId: string; at: number } | null>(null);
  // The selection box being drawn across the lanes, in the timeline's own pixels
  const [selectionBox, setSelectionBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  /** How much the canvas zoom scales the timeline on screen; 1 on the Edit page. Pointer distances are divided by it. */
  const screenScale = (): number => {
    const content = contentRef.current;
    if (!content || content.offsetWidth === 0) return 1;
    return content.getBoundingClientRect().width / content.offsetWidth || 1;
  };

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    setViewportWidth(scroller.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setViewportWidth(scroller.clientWidth));
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  // The playback engine moves the playhead directly, without a React render per frame.
  useEffect(() => {
    const move = (timeSec: number, playing: boolean) => {
      const x = timeSec * pps;
      if (playheadEl.current) playheadEl.current.style.transform = `translateX(${x}px)`;
      const scroller = scrollRef.current;
      if (playing && scroller && x > scroller.scrollLeft + scroller.clientWidth - 40) scroller.scrollLeft = x - 80;
    };
    registerPlayhead(move);
    move(getTime(), false);
    return () => registerPlayhead(null);
  }, [pps, registerPlayhead, getTime]);

  // Wheel scrolls the timeline sideways; with Ctrl it zooms around the pointer. The listener
  // is native and stops the event so the canvas underneath does not pan or zoom.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // With Shift the tracks scroll up and down, when there are more than fit
      if (e.shiftKey) {
        if (rootRef.current) rootRef.current.scrollTop += e.deltaY || e.deltaX;
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        const offsetX = (e.clientX - scroller.getBoundingClientRect().left) / screenScale();
        const time = (scroller.scrollLeft + offsetX) / pps;
        const next = clamp(pps * Math.exp(-e.deltaY * 0.002), MIN_PX_PER_SEC, MAX_PX_PER_SEC);
        if (Math.abs(next - pps) > 0.01) {
          zoomAnchorRef.current = { time, offsetX };
          onZoom(next);
        }
      } else {
        scroller.scrollLeft += Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      }
    };
    scroller.addEventListener('wheel', onWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', onWheel);
  }, [pps, onZoom]);

  // After a zoom, keep the moment that was under the pointer where it was.
  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current;
    const scroller = scrollRef.current;
    if (!anchor || !scroller) return;
    scroller.scrollLeft = Math.max(0, anchor.time * pps - anchor.offsetX);
    zoomAnchorRef.current = null;
  }, [pps]);

  const timeAt = (clientX: number): number => {
    const rect = contentRef.current?.getBoundingClientRect();
    return rect ? Math.max(0, (clientX - rect.left) / screenScale() / pps) : 0;
  };

  const trackAt = (clientY: number, kind: TrackKind): string | undefined => {
    for (const [trackId, el] of laneEls.current) {
      const rect = el.getBoundingClientRect();
      if (clientY >= rect.top && clientY < rect.bottom) {
        return project.tracks.find(track => track.id === trackId)?.kind === kind ? trackId : undefined;
      }
    }
    return undefined;
  };

  /** The time dragged media would start at: under the pointer, snapped to clip edges and the playhead. */
  const dropTime = (clientX: number): number => {
    const time = timeAt(clientX);
    if (!project.snapping || !dragged) return time;
    return Math.max(0, snapMove(time, dragged.durationSec, snapPoints(project, [], [getTime()]), SNAP_PX / pps));
  };

  /**
   * Every lane takes a drop: a clip lands on the lane when it is of the clip's kind, else on a
   * track of its kind at that time (utils/quickEditor.ts insertAsset); the strip for a new
   * layer makes a track; the empty bed and the ruler put it on the first free track.
   */
  const dropHandlers = (lane: string, kind: TrackKind | null) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!acceptsDrops) return;
      const files = isFileDrag(e);
      if (!mediaDrag && !files) return;
      if (files && !onDropFiles) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
      if (files && !fileDrag) setFileDrag(true);
      const timeSec = dropTime(e.clientX);
      setDropHint(prev => (prev && prev.lane === lane && Math.abs(prev.timeSec - timeSec) < 1e-3 ? prev : { lane, timeSec }));
    },
    onDragLeave: (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setDropHint(prev => (prev?.lane === lane ? null : prev));
    },
    onDrop: (e: React.DragEvent) => {
      if (!acceptsDrops) return;
      const files = isFileDrag(e) ? Array.from(e.dataTransfer.files) : null;
      if (!mediaDrag && !files) return;
      e.preventDefault();
      e.stopPropagation();
      const timeSec = dropTime(e.clientX);
      setDropHint(null);
      setFileDrag(false);
      const drop: MediaDrop = lane === NEW_LANE ? { newTrack: true, timeSec } : lane === BED_LANE ? { timeSec } : { trackId: lane, timeSec };
      if (files) { if (files.length > 0) onDropFiles?.(files, drop); return; }
      // A lane of another kind than the media: the clip goes to a track of its own kind at that time
      if (kind !== null && mediaDrag!.kind !== kind) { onDropMedia!({ timeSec }); return; }
      onDropMedia!(drop);
    },
  });

  // The drag left the timeline altogether: no files are over it any more
  const onRootDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setFileDrag(false);
    setDropHint(null);
  };

  /** Where dropped media would land on a lane, shown while it is dragged over it. */
  const renderGhost = (lane: string, startSec: number) => (
    dropHint?.lane === lane && dragged ? (
      <div
        className="absolute top-1 bottom-1 rounded-md bg-cream/15 ring-1 ring-cream/60 pointer-events-none z-10"
        style={{ left: startSec * pps, width: Math.max(4, dragged.durationSec * pps) }}
        data-drop-ghost
      />
    ) : null
  );

  const startSeek = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault(); // a drag, not the start of a text selection
    dragRef.current = {
      mode: 'seek', pointerId: e.pointerId, originX: e.clientX, originY: e.clientY, originStart: 0, originEnd: 0,
      base: project, moved: false, last: null,
    };
    contentRef.current?.setPointerCapture(e.pointerId);
    onSeek(timeAt(e.clientX));
  };

  /** Empty lane space: a click moves the playhead and clears the selection, a drag draws a selection box. */
  const startBox = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault(); // a drag, not the start of a text selection
    const content = contentRef.current;
    if (!content) return;
    const rect = content.getBoundingClientRect();
    const scale = screenScale();
    dragRef.current = {
      mode: 'box', pointerId: e.pointerId, originX: e.clientX, originY: e.clientY, originStart: 0, originEnd: 0,
      base: project, moved: false, last: null,
      boxOrigin: { x: (e.clientX - rect.left) / scale, y: (e.clientY - rect.top) / scale },
      // With Shift or Ctrl the box adds to what is already selected
      keep: e.shiftKey || e.ctrlKey || e.metaKey ? selectedIds : [],
    };
    content.setPointerCapture(e.pointerId);
  };

  const onClipPointerDown = (e: React.PointerEvent, clip: EditorClip, zone: 'body' | 'start' | 'end') => {
    if (e.button !== 0) return;
    e.preventDefault(); // a drag, not the start of a text selection
    e.stopPropagation();
    if (tool === 'razor') {
      if (disabled) return;
      let at = timeAt(e.clientX);
      if (project.snapping) at = snapTime(at, [getTime()], SNAP_PX / pps);
      const next = splitClip(project, clip.id, at, makeId);
      if (next !== project) onCommit(next);
      return;
    }
    // Shift or Ctrl (Cmd on a Mac) and a click adds the clip to the selection or takes it out
    const toggle = e.shiftKey || e.ctrlKey || e.metaKey;
    let selection = selectedIds;
    if (toggle && selected.has(clip.id)) {
      onSelect(selectedIds.filter(id => id !== clip.id));
      return; // taken out: nothing to drag
    }
    if (toggle) selection = [...selectedIds, clip.id];
    else if (!selected.has(clip.id)) selection = [clip.id];
    if (selection !== selectedIds) onSelect(selection);
    if (disabled) return;
    dragRef.current = {
      mode: zone === 'body' ? 'move' : zone === 'start' ? 'trim-start' : 'trim-end',
      pointerId: e.pointerId,
      clipId: clip.id,
      kind: project.tracks.find(track => track.id === clip.trackId)?.kind,
      originX: e.clientX,
      originY: e.clientY,
      originStart: clip.startSec,
      originEnd: clipEnd(clip),
      base: project,
      moved: false,
      last: null,
      together: zone === 'body' && selection.length > 1 ? selection : undefined,
      collapseTo: !toggle && selection.length > 1 ? clip.id : undefined,
    };
    contentRef.current?.setPointerCapture(e.pointerId);
  };

  /** Grabbing a clip's volume line: dragging it up or down sets the clip's gain. */
  const onGainPointerDown = (e: React.PointerEvent<HTMLElement>, clip: EditorClip) => {
    if (e.button !== 0 || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (!selected.has(clip.id)) onSelect([clip.id]);
    // A second press soon after the first is a double-click: back to 0 dB. (The browser's own
    // dblclick goes to the timeline, which holds the pointer during the first press.)
    const now = performance.now();
    const previous = lastGainPress.current;
    lastGainPress.current = { clipId: clip.id, at: now };
    if (previous && previous.clipId === clip.id && now - previous.at < 400) {
      lastGainPress.current = null;
      if (clipGainDb(clip) !== 0) onCommit(setClipGain(project, [clip.id], 0));
      return;
    }
    // The clip's box on screen already includes any canvas zoom
    const box = (e.currentTarget.closest('[data-clip-id]') as HTMLElement | null)?.getBoundingClientRect();
    const scale = box && box.height > 0 ? box.height / (trackHeight.audio - 8) : 1;
    dragRef.current = {
      mode: 'gain', pointerId: e.pointerId, clipId: clip.id, originX: e.clientX, originY: e.clientY, originStart: 0, originEnd: 0,
      originDb: clipGainDb(clip), spanPx: (trackHeight.audio - 8 - GAIN_LINE_TOP - GAIN_LINE_BOTTOM) * scale,
      base: project, moved: false, last: null,
    };
    contentRef.current?.setPointerCapture(e.pointerId);
    setGainReadout({ clipId: clip.id, db: clipGainDb(clip) });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (drag.mode === 'seek') {
      onSeek(timeAt(e.clientX));
      return;
    }
    if (drag.mode === 'box') {
      if (!drag.moved && Math.abs(e.clientX - drag.originX) < 4 && Math.abs(e.clientY - drag.originY) < 4) return;
      drag.moved = true;
      const content = contentRef.current;
      if (!content || !drag.boxOrigin) return;
      // Measured on screen, so it holds at any canvas zoom and after the lanes scroll
      const rect = content.getBoundingClientRect();
      const scale = screenScale();
      const startX = rect.left + drag.boxOrigin.x * scale;
      const startY = rect.top + drag.boxOrigin.y * scale;
      const left = Math.min(startX, e.clientX);
      const right = Math.max(startX, e.clientX);
      const top = Math.min(startY, e.clientY);
      const bottom = Math.max(startY, e.clientY);
      setSelectionBox({ left: (left - rect.left) / scale, top: (top - rect.top) / scale, width: (right - left) / scale, height: (bottom - top) / scale });
      const touched: string[] = [];
      content.querySelectorAll<HTMLElement>('[data-clip-id]').forEach((el) => {
        const box = el.getBoundingClientRect();
        if (box.right > left && box.left < right && box.bottom > top && box.top < bottom && el.dataset.clipId) touched.push(el.dataset.clipId);
      });
      const next = [...new Set([...(drag.keep ?? []), ...touched])];
      if (next.length !== selectedIds.length || next.some((id, i) => id !== selectedIds[i])) onSelect(next);
      return;
    }
    if (drag.mode === 'in' || drag.mode === 'out') {
      if (!drag.moved && Math.abs(e.clientX - drag.originX) < 2) return;
      drag.moved = true;
      let at = drag.originStart + (e.clientX - drag.originX) / screenScale() / pps;
      if (drag.base.snapping) at = snapTime(at, snapPoints(drag.base, [], [getTime()]), SNAP_PX / pps);
      drag.last = drag.mode === 'in' ? setInPoint(drag.base, at) : setOutPoint(drag.base, at);
      onPreview(drag.last);
      return;
    }
    if (drag.mode === 'gain') {
      const dy = e.clientY - drag.originY;
      if (!drag.moved && Math.abs(dy) < 2) return;
      drag.moved = true;
      // Half-decibel steps; with Shift, finer
      const raw = (drag.originDb ?? 0) + (dy / (drag.spanPx || 1)) * MIN_GAIN_DB * (e.shiftKey ? 0.2 : 1);
      const db = Math.min(0, Math.max(MIN_GAIN_DB, Math.round(raw * (e.shiftKey ? 10 : 2)) / (e.shiftKey ? 10 : 2)));
      drag.last = setClipGain(drag.base, [drag.clipId!], db);
      setGainReadout({ clipId: drag.clipId!, db });
      onPreview(drag.last);
      return;
    }
    const dx = e.clientX - drag.originX;
    if (!drag.moved && Math.abs(dx) < 3 && Math.abs(e.clientY - drag.originY) < 6) return;
    drag.moved = true;
    const base = drag.base;
    const clip = base.clips.find(c => c.id === drag.clipId);
    if (!clip) return;
    const deltaSec = dx / screenScale() / pps;
    const threshold = SNAP_PX / pps;
    // Everything that moves is left out of the snap points: the grabbed clip, the selection, what is linked to them
    const moving = (drag.together ?? [clip.id]).flatMap(id => linkedGroup(base, id).map(c => c.id));
    const points = base.snapping ? snapPoints(base, moving, [getTime()]) : [];

    let next = base;
    if (drag.mode === 'move') {
      let desired = drag.originStart + deltaSec;
      if (base.snapping) desired = snapMove(desired, clipDuration(clip), points, threshold);
      next = moveClips(base, drag.together ?? [clip.id], clip.id, desired, drag.kind ? trackAt(e.clientY, drag.kind) : undefined);
    } else if (drag.mode === 'trim-start') {
      const to = drag.originStart + deltaSec;
      next = trimClip(base, clip.id, 'start', base.snapping ? snapTime(to, points, threshold) : to);
    } else {
      const to = drag.originEnd + deltaSec;
      next = trimClip(base, clip.id, 'end', base.snapping ? snapTime(to, points, threshold) : to);
    }
    drag.last = next;
    onPreview(next);
  };

  const endDrag = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    if (contentRef.current?.hasPointerCapture(e.pointerId)) contentRef.current.releasePointerCapture(e.pointerId);
    if (drag.mode === 'gain') setGainReadout(null);
    if (drag.mode === 'seek') return;
    if (drag.mode === 'box') {
      setSelectionBox(null);
      // Just a click: the playhead goes there, and without Shift or Ctrl the selection is cleared
      if (!drag.moved) {
        if (!drag.keep?.length) onSelect([]);
        onSeek(timeAt(e.clientX));
      }
      return;
    }
    if (drag.moved && drag.last && drag.last !== drag.base) onCommit(drag.last);
    else {
      onCancelPreview();
      if (!drag.moved && drag.collapseTo) onSelect([drag.collapseTo]);
    }
  };

  /** An in or out marker on the ruler: drag it along to move the point. */
  const renderMarker = (which: 'in' | 'out', atSec: number) => (
    <div
      className={`absolute top-0 bottom-0 w-3 z-10 cursor-ew-resize ${which === 'in' ? '' : '-ml-3'}`}
      style={{ left: atSec * pps }}
      onPointerDown={(e) => {
        if (e.button !== 0 || disabled) return;
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = {
          mode: which, pointerId: e.pointerId, originX: e.clientX, originY: e.clientY, originStart: atSec, originEnd: atSec,
          base: project, moved: false, last: null,
        };
        contentRef.current?.setPointerCapture(e.pointerId);
      }}
      title={`${which === 'in' ? 'In' : 'Out'} point ${formatTimecode(atSec)}: drag to move`}
      data-export-marker={which}
    >
      {/* A bracket: [ for in, ] for out */}
      <div className={`absolute inset-y-0 w-[3px] bg-lavender ${which === 'in' ? 'left-0 rounded-l' : 'right-0 rounded-r'}`} />
      <div className={`absolute top-0 h-[3px] w-full bg-lavender`} />
      <div className={`absolute bottom-0 h-[3px] w-full bg-lavender`} />
    </div>
  );

  const step = rulerSteps(frameRate).find(s => s * pps >= 64) ?? 600;
  const framePx = pps / frameRate;
  const tickCount = Math.min(400, Math.ceil(contentWidth / (step * pps)) + 1);

  const renderHeader = (track: EditorTrack) => {
    const isVideo = track.kind === 'video';
    const off = isVideo ? !!track.hidden : !!track.muted;
    const removable = !project.clips.some(clip => clip.trackId === track.id) && project.tracks.filter(t => t.kind === track.kind).length > 1;
    const ToggleIcon = isVideo ? (off ? EyeOff : Eye) : (off ? VolumeX : Volume2);
    const level = trackLevel(track);
    return (
      <div key={track.id} className="group flex flex-col justify-center px-1.5 border-b border-white/5 text-2xs font-semibold text-ink-faint" style={{ height: trackHeight[track.kind] }} data-track-header={track.id}>
        <div className="flex items-center gap-1 min-w-0">
        {isVideo ? <Film size={12} className="shrink-0" aria-hidden="true" /> : <AudioLines size={12} className="shrink-0" aria-hidden="true" />}
        <span className={`min-w-0 truncate ${off ? 'text-ink-faint line-through' : 'text-cream'}`}>{track.name}</span>
        <span className="ml-auto -mr-1 flex items-center shrink-0">
          {removable && !disabled && (
            <button
              type="button"
              onClick={() => onCommit(removeTrack(project, track.id))}
              className={`node-reveal ${EDITOR_REMOVE_BTN} opacity-0 group-hover:opacity-100 focus-visible:opacity-100`}
              aria-label={`Remove track ${track.name}`}
              title="Remove empty track"
            >
              <Trash2 />
            </button>
          )}
          <button
            type="button"
            onClick={() => onCommit(setTrackFlag(project, track.id, isVideo ? 'hidden' : 'muted', !off))}
            // Pressed while the track is hidden or muted: lavender, like every tool that is on
            className={`${EDITOR_ICON_BTN} ns-icon-btn-xs`}
            aria-label={isVideo ? (off ? `Show track ${track.name}` : `Hide track ${track.name}`) : (off ? `Unmute track ${track.name}` : `Mute track ${track.name}`)}
            aria-pressed={off}
            title={isVideo ? (off ? 'Show in preview' : 'Hide from preview') : (off ? 'Unmute' : 'Mute')}
            disabled={disabled}
          >
            <ToggleIcon />
          </button>
        </span>
        </div>
        {/* A video layer's opacity over the layers below, or an audio track's volume in the mix */}
        <div className="flex items-center gap-1.5" title={isVideo ? 'Layer opacity' : 'Track volume'}>
          <div className="flex-1 min-w-0">
            <MediaSlider
              value={level}
              label={isVideo ? `${track.name} opacity` : `${track.name} volume`}
              valueText={`${Math.round(level * 100)}%`}
              disabled={disabled}
              step={0.05}
              onStart={() => { levelDrag.current = { base: project, last: project }; }}
              onChange={(value) => {
                const drag = levelDrag.current;
                if (!drag) { onCommit(setTrackLevel(project, track.id, value)); return; }
                drag.last = setTrackLevel(drag.base, track.id, value);
                onPreview(drag.last);
              }}
              onEnd={() => {
                const drag = levelDrag.current;
                levelDrag.current = null;
                if (drag && drag.last !== drag.base) onCommit(drag.last);
                else onCancelPreview();
              }}
              dataName={`level-${track.id}`}
            />
          </div>
          <span className="w-8 shrink-0 text-right font-medium tabular-nums" data-track-level>{Math.round(level * 100)}%</span>
        </div>
      </div>
    );
  };

  /** The strip for a new layer, shown while media is dragged: above the video layers, or below the audio tracks. */
  const newLaneHeader = () => (
    <div className="flex items-center gap-1.5 px-1.5 border-b border-white/5 text-2xs font-semibold text-lavender" style={{ height: NEW_LANE_HEIGHT }}>
      <Layers size={12} aria-hidden="true" /> New
    </div>
  );
  const newLane = (kind: TrackKind) => (
    <div
      className={`relative border-b border-white/5 transition-colors ${dropHint?.lane === NEW_LANE ? 'bg-lavender/25' : 'bg-lavender/[0.08]'}`}
      style={{ height: NEW_LANE_HEIGHT }}
      data-new-lane={kind}
      {...dropHandlers(NEW_LANE, kind)}
    >
      <span className="sticky left-2 inline-flex items-center gap-1.5 h-full px-2 text-2xs font-semibold text-lavender whitespace-nowrap pointer-events-none">
        <Plus size={12} aria-hidden="true" /> Drop here for a new {kind === 'video' ? 'video layer' : 'audio track'}
      </span>
      {renderGhost(NEW_LANE, dropHint?.timeSec ?? 0)}
    </div>
  );

  const renderClip = (clip: EditorClip, kind: TrackKind) => {
    const asset = assetsById.get(clip.assetId);
    const width = Math.max(4, clipDuration(clip) * pps);
    const isSelected = selected.has(clip.id);
    const missing = missingAssetIds.has(clip.assetId);
    const canEdit = tool === 'select' && !disabled;
    const wave = kind === 'audio' && !missing ? waveforms[clip.assetId] : undefined;
    const waveState = kind === 'audio' && !missing && !wave ? waveformStatus[clip.assetId] : undefined;
    const track = project.tracks.find(t => t.id === clip.trackId);
    const gainDb = clipGainDb(clip);
    const innerHeight = trackHeight.audio - 8;
    const gainY = gainLineY(gainDb, innerHeight);
    const readout = gainReadout?.clipId === clip.id ? gainReadout.db : null;
    return (
      <div
        key={clip.id}
        data-clip-id={clip.id}
        data-selected={isSelected || undefined}
        className={`absolute top-1 bottom-1 rounded-md overflow-hidden select-none ${kind === 'video' ? 'bg-lavender' : 'bg-mint'} text-ink ${
          isSelected ? 'ring-2 ring-cream z-10' : 'ring-1 ring-ink/30'
        } ${missing ? 'opacity-60' : ''} ${tool === 'razor' ? 'cursor-crosshair' : 'cursor-grab active:cursor-grabbing'}`}
        style={{ left: clip.startSec * pps, width }}
        title={`${asset?.name || 'Clip'} · ${formatTimecode(clipDuration(clip))}${missing ? ' · media missing' : ''}${
          waveState === 'reading' ? ' · reading the sound' : waveState === 'none' ? ' · no sound could be read' : ''
        }`}
        data-waveform-state={wave ? 'ready' : waveState}
        onPointerDown={(e) => onClipPointerDown(e, clip, 'body')}
      >
        {wave && (
          <ClipWaveform
            peaks={wave}
            inSec={clip.inSec}
            durationSec={clipDuration(clip)}
            pxPerSec={pps}
            height={innerHeight}
            gain={(track ? trackLevel(track) : 1) * dbToGain(gainDb)}
          />
        )}
        {/* While the sound is read a line pulses; with no sound to read it stays flat */}
        {waveState && (
          <div className={`absolute inset-x-1.5 bottom-2 h-px pointer-events-none ${waveState === 'reading' ? 'bg-ink/40 animate-pulse' : 'bg-ink/25'}`} aria-hidden="true" />
        )}
        {/* The volume line itself, under the name label */}
        {kind === 'audio' && !missing && (
          <div className="absolute inset-x-1.5 h-px bg-ink/70 pointer-events-none" style={{ top: gainY }} aria-hidden="true" data-gain-level={gainDb} />
        )}
        {width >= 30 && (
          // Over a waveform the name sits in a small label at the top, clear of the peaks
          <div className={`relative z-10 flex gap-1 h-full text-2xs font-semibold overflow-hidden pointer-events-none whitespace-nowrap ${wave ? 'items-start pt-0.5 px-1' : 'items-center px-2.5'}`}>
            <span className={`flex items-center gap-1 min-w-0 ${wave ? 'px-1 rounded-sm bg-mint' : ''}`}>
              {missing ? <AlertTriangle size={12} className="shrink-0" /> : kind === 'video' ? <Film size={12} className="shrink-0" /> : <AudioLines size={12} className="shrink-0" />}
              <span className="truncate">{asset?.name || 'Clip'}</span>
              {clip.linkId && <Link2 size={12} className="shrink-0 opacity-60" aria-label="Linked" />}
              {kind === 'audio' && gainDb < 0 && (
                <span className="shrink-0 ml-0.5 px-1 rounded-sm bg-ink/80 text-cream font-medium tabular-nums" data-clip-gain>{formatDb(gainDb)}</span>
              )}
            </span>
          </div>
        )}
        {/* The clip's volume line: drag it up or down to make this clip quieter; double-click for 0 dB */}
        {kind === 'audio' && !missing && (
          <>
            {canEdit && (
              <div
                className="absolute inset-x-1.5 h-[9px] -mt-[4px] cursor-ns-resize group/gain"
                style={{ top: gainY }}
                onPointerDown={(e) => onGainPointerDown(e, clip)}
                title={`Volume ${formatDb(gainDb)}: drag up or down, double-click for 0 dB`}
                data-gain-line
              >
                <div className={`absolute left-0 w-full top-[3px] h-[3px] rounded-full transition-colors ${readout !== null ? 'bg-flame' : 'bg-transparent group-hover/gain:bg-flame/80'}`} />
              </div>
            )}
            {readout !== null && (
              <span
                className="absolute right-2 z-20 px-1.5 rounded-sm bg-ink text-cream text-2xs font-semibold tabular-nums pointer-events-none"
                style={{ top: Math.min(gainY + 4, innerHeight - 14) }}
                data-gain-readout
              >
                {formatDb(readout)}
              </span>
            )}
          </>
        )}
        {canEdit && (
          <>
            <div className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize bg-ink/15 hover:bg-ink/45" onPointerDown={(e) => onClipPointerDown(e, clip, 'start')} />
            <div className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize bg-ink/15 hover:bg-ink/45" onPointerDown={(e) => onClipPointerDown(e, clip, 'end')} />
          </>
        )}
      </div>
    );
  };

  return (
    // Many layers scroll up and down (wheel over the track names, or Shift and the wheel over the lanes)
    <div
      ref={rootRef}
      // The bed is the editor's ink, a shade deeper
      className={`node-scrollable-content no-scrollbar shrink-0 select-none overflow-y-auto bg-black/30 text-cream border-t border-white/10 ${roomy ? 'min-h-[42%] max-h-[62%]' : 'max-h-[58%]'}`}
      onDragLeave={onRootDragLeave}
      data-editor-timeline
      data-file-drag={fileDrag ? '1' : undefined}
    >
      {/* The names and the lanes grow together with the tracks; only the timeline around them scrolls */}
      <div className="flex min-h-full">
      <div className="shrink-0 border-r border-white/10" style={{ width: headerWidth }}>
        <div className="border-b border-white/10" style={{ height: RULER_HEIGHT }} />
        {acceptsDrops && dragged?.kind === 'video' && newLaneHeader()}
        {project.tracks.map(renderHeader)}
        {acceptsDrops && dragged?.kind === 'audio' && newLaneHeader()}
      </div>

      {/* No scrollbar: the wheel moves the timeline sideways and playback follows the playhead */}
      <div ref={scrollRef} className="no-scrollbar flex-1 min-w-0 overflow-x-auto overflow-y-hidden">
        <div
          ref={contentRef}
          className="relative min-h-full"
          style={{ width: contentWidth }}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div
            className="relative border-b border-white/10 cursor-col-resize touch-none"
            style={{
              height: RULER_HEIGHT,
              // A short tick on every frame when zoomed in enough to see them
              ...(framePx >= FRAME_TICK_MIN_PX ? {
                backgroundImage: `repeating-linear-gradient(to right, rgba(249, 246, 241, 0.16) 0 1px, transparent 1px ${framePx}px)`,
                backgroundSize: '100% 5px',
                backgroundPosition: 'left bottom',
                backgroundRepeat: 'no-repeat',
              } : {}),
            }}
            onPointerDown={startSeek}
            {...(acceptsDrops ? dropHandlers(BED_LANE, null) : {})}
            data-editor-ruler
            data-frame-ticks={framePx >= FRAME_TICK_MIN_PX ? '1' : undefined}
          >
            {/* The part between the in and out points, which is exported */}
            {range.marked && (
              <div className="absolute inset-y-0 bg-lavender/20 border-x border-lavender/60 pointer-events-none" style={{ left: range.startSec * pps, width: (range.endSec - range.startSec) * pps }} data-export-range />
            )}
            {Array.from({ length: tickCount }, (_, i) => (
              <div key={i} className="absolute inset-y-0 border-l border-white/15 pl-1 text-2xs leading-[22px] font-medium text-ink-faint tabular-nums whitespace-nowrap pointer-events-none" style={{ left: i * step * pps }}>
                {formatFrameTimecode(i * step, frameRate)}
              </div>
            ))}
            {project.inSec !== undefined && renderMarker('in', range.startSec)}
            {project.outSec !== undefined && renderMarker('out', range.endSec)}
          </div>

          {acceptsDrops && dragged?.kind === 'video' && newLane('video')}
          {project.tracks.map((track) => (
            <div
              key={track.id}
              ref={(el) => { if (el) laneEls.current.set(track.id, el); else laneEls.current.delete(track.id); }}
              className={`relative border-b border-white/5 touch-none ${(track.hidden || track.muted) ? 'opacity-50' : ''} ${
                acceptsDrops && dropHint?.lane === track.id ? (dragged && dragged.kind === track.kind ? 'bg-lavender/10' : 'bg-white/[0.06]') : track.kind === 'video' ? 'bg-white/[0.03]' : ''
              }`}
              style={{ height: trackHeight[track.kind] }}
              data-track-id={track.id}
              onPointerDown={startBox}
              {...dropHandlers(track.id, track.kind)}
            >
              {project.clips.filter(clip => clip.trackId === track.id).map(clip => renderClip(clip, track.kind))}
              {dragged && dragged.kind === track.kind && dropHint?.lane === track.id && renderGhost(track.id, placeOnTrack(project, track.id, dropHint.timeSec, dragged.durationSec))}
            </div>
          ))}
          {acceptsDrops && dragged?.kind === 'audio' && newLane('audio')}

          {/* The empty bed under the tracks: a drop here goes on the first free track at that time */}
          {acceptsDrops && (
            <div
              className={`relative ${dropHint?.lane === BED_LANE ? 'bg-lavender/10' : ''}`}
              style={{ minHeight: NEW_LANE_HEIGHT * 2 }}
              data-timeline-bed
              {...dropHandlers(BED_LANE, null)}
            >
              {dropHint?.lane === BED_LANE && (
                <span className="sticky left-2 inline-flex items-center gap-1.5 px-2 py-1.5 text-2xs font-semibold text-lavender whitespace-nowrap pointer-events-none">
                  <Plus size={12} aria-hidden="true" /> Drop here: on the first free track at {formatTimecode(dropHint.timeSec)}
                </span>
              )}
            </div>
          )}

          {/* Outside the in and out points the lanes are dimmed: that part is not exported */}
          {range.marked && (
            <>
              <div className="absolute left-0 bottom-0 bg-black/45 pointer-events-none z-[15]" style={{ top: RULER_HEIGHT, width: range.startSec * pps }} />
              <div className="absolute right-0 bottom-0 bg-black/45 pointer-events-none z-[15]" style={{ top: RULER_HEIGHT, left: range.endSec * pps }} />
            </>
          )}

          {selectionBox && (
            <div
              className="absolute rounded-sm bg-cream/10 ring-1 ring-cream/80 pointer-events-none z-30"
              style={selectionBox}
              data-selection-box
            />
          )}

          <div ref={playheadEl} className="absolute top-0 bottom-0 left-0 w-px bg-flame pointer-events-none z-20" data-editor-playhead>
            <div className="absolute top-0 -left-[5px] w-[11px] h-2.5 rounded-b bg-flame" />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
});
EditorTimeline.displayName = 'EditorTimeline';

export default EditorTimeline;
